import type { OwnAction } from '../model/intel.js';
import { BODY, DATA, DATA_LABEL } from '../../theme/type.js';

/**
 * What this seat's orders did (design's `orders[]`).
 *
 * The most valuable block on the screen and the one that needed the most care
 * about where it comes from. `model/intel.ts`'s `ownActionsOf` has the argument;
 * the short version is that `orders.state` carries the turn being **played** and
 * a report is always about an earlier one, so the join the design implies can
 * never succeed — and this block is built from the report's own
 * `MISSION_RESOLVED` and `ORDER_INVALIDATED` entries instead.
 *
 * `ORDER_INVALIDATED`'s reason is carried **verbatim**, which is
 * `projection/intel.ts`'s own instruction: *"§7.3: 'why did nothing happen?' is
 * the worst possible after-action outcome, so the reason is carried verbatim and
 * the client renders it first."* Not looked up, not rewritten, and not summarised
 * — the engine wrote a sentence naming what was held and what was needed, and
 * this console's job is to put it in front of the person who has to fix it.
 *
 * **The heading names what the block holds and not what the seat did.** There is
 * no "3 OF 4 ORDERS" here: an order can produce no entry at all (a resolver that
 * skips a missing target says nothing), and this console cannot see one — so a
 * denominator would be a count of orders nobody has.
 */

export interface OrdersBlockProps {
  readonly actions: readonly OwnAction[];
  readonly onSelect: (id: string) => void;
}

export function OrdersBlock(props: OrdersBlockProps): JSX.Element | null {
  if (props.actions.length === 0) return null;

  return (
    <section data-testid="intel-orders">
      <div
        style={{
          ...DATA_LABEL,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          height: 27,
          padding: '0 12px',
          background: 'var(--surface-2)',
          borderBottom: '1px solid var(--border)',
          fontSize: 10.5,
          color: 'var(--text-2)',
        }}
      >
        <span>YOUR ORDERS</span>
        <span style={{ flex: 1 }} />
        <span style={{ color: 'var(--text-muted)' }}>{props.actions.length}</span>
      </div>

      {props.actions.map((action) => (
        <div
          key={action.key}
          data-testid="intel-order"
          data-executed={action.executed}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            padding: '7px 12px 8px',
            borderBottom: '1px solid var(--rule-faint)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              data-testid="intel-order-asset"
              onClick={() => props.onSelect(action.asset)}
              style={{
                ...DATA,
                flex: 'none',
                padding: 0,
                background: 'transparent',
                border: 'none',
                wordBreak: 'break-word',
                // Blue, not accent — see EntryRow's SubjectButton: a link, not a
                // selection, so cyan stays free for the thing actually picked.
                color: 'var(--blue)',
                cursor: 'pointer',
              }}
            >
              {action.asset}
            </button>
            {/* The mission is the payload's own scalar, rendered as it arrived.
                Not `MISSION_NAMES[…]`: that table is keyed on `MissionId` and this
                is a `string` off the wire, so a lookup would silently blank a
                mission this build's enum does not have. */}
            {action.mission !== null && (
              <span
                data-testid="intel-order-mission"
                style={{
                  ...DATA,
                  flex: 'none',
                  fontSize: 11.5,
                  wordBreak: 'break-word',
                  color: 'var(--text)',
                }}
              >
                {action.mission}
              </span>
            )}
            {action.contact !== null && (
              <>
                <span style={{ flex: 'none', color: 'var(--text-muted)', fontSize: 11 }}>›</span>
                <button
                  type="button"
                  data-testid="intel-order-target"
                  onClick={() => props.onSelect(action.contact ?? '')}
                  style={{
                    ...DATA,
                    flex: 'none',
                    padding: 0,
                    background: 'transparent',
                    border: 'none',
                    wordBreak: 'break-word',
                    color: 'var(--blue)',
                    cursor: 'pointer',
                  }}
                >
                  {action.contact}
                </button>
              </>
            )}
            <span style={{ flex: 1 }} />
            <span
              data-testid="intel-order-verdict"
              style={{
                ...DATA,
                flex: 'none',
                fontSize: 10.5,
                color: action.executed
                  ? action.outcome === 'FAILURE' || action.outcome === 'COMPLICATION'
                    ? 'var(--warn)'
                    : 'var(--ok)'
                  : 'var(--red-text)',
              }}
            >
              {action.executed ? (action.outcome ?? 'RESOLVED') : (action.reason ?? 'NOT EXECUTED')}
            </span>
          </div>
          {action.detail !== null && (
            <div
              data-testid="intel-order-detail"
              style={{ ...BODY, fontSize: 11.5, lineHeight: 1.35, color: 'var(--text-2)' }}
            >
              {action.detail}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}
